//
// QnsIcon — small inline SVG marker shown next to addresses whose displayed
// name came from QNS (the QIE Name Service).
//
// Why inline rather than via icons/QIE.svg + sprite:
//   The Next.js build on this project (yarn build = next build) does NOT
//   regenerate the icon sprite. Adding a new icon file to icons/ would
//   require running `yarn svg:build-sprite` first AND updating
//   NEXT_PUBLIC_ICON_SPRITE_HASH in envs.js. Inlining the SVG sidesteps
//   both steps and removes that build coupling entirely.
//
// Design: rounded badge background (15% opacity of current text colour) with
// a "Q" monogram in current text colour. Looks native in both light + dark
// themes because everything is derived from currentColor.
//
import { chakra, Tooltip } from '@chakra-ui/react';
import React from 'react';

interface Props {
  // Default 4 (Chakra spacing → 16px). Matches the visual weight of the
  // existing ENS icons next to names.
  boxSize?: number | string;
  // Tooltip text shown on hover. Omitted → no tooltip wrapper.
  tooltipLabel?: React.ReactNode;
}

const QnsIconBase = ({ boxSize = 4 }: Pick<Props, 'boxSize'>) => (
  <chakra.svg
    viewBox="0 0 30 30"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    boxSize={ boxSize }
    flexShrink={ 0 }
    display="inline-block"
    verticalAlign="middle"
    aria-label="QIE Name Service"
  >
    <rect x="3" y="3" width="24" height="24" rx="6" fill="currentColor" opacity="0.15"/>
    <circle cx="14.5" cy="14.5" r="5.5" stroke="currentColor" strokeWidth="2.2" fill="none"/>
    <path d="M18.7 18.5 L22 21.8" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"/>
  </chakra.svg>
);

const QnsIcon = ({ boxSize, tooltipLabel }: Props) => {
  if (tooltipLabel) {
    return (
      <Tooltip label={ tooltipLabel } placement="top">
        <chakra.span display="inline-flex" alignItems="center">
          <QnsIconBase boxSize={ boxSize }/>
        </chakra.span>
      </Tooltip>
    );
  }
  return <QnsIconBase boxSize={ boxSize }/>;
};

export default React.memo(QnsIcon);